import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { API_KEY, LIVE_API_BASE_URL, getApiHeaders } from '../config/api';
import { packPriceLabel, pricingChangeWarning, validatePackPrice } from '../utils/stickerPackPricing';

const liveApiBase = `${LIVE_API_BASE_URL.replace(/\/$/, '')}/v1/live`;

export type StickerTextSlot = {
  x: number;
  y: number;
  width: number;
  height: number;
  fontKey: string;
  colorHex: string;
  align: string;
  maxLines: number;
  maxFontSize: number;
  minFontSize: number;
};

export type StickerItem = {
  id: string;
  packId: string;
  name: string;
  imageUrl: string;
  kind: string;
  aspectRatio: number;
  isActive: boolean;
  sortOrder: number;
  textSlot?: StickerTextSlot | null;
  /** Read-only: its pack's status. */
  isPremium?: boolean;
};

export type StickerPack = {
  id: string;
  name: string;
  iconUrl: string | null;
  sortOrder: number;
  isActive: boolean;
  /** Paid pack: users unlock it by buying it with tokens. */
  isPremium?: boolean;
  priceTokens?: number | null;
  /** Users who bought it. Kept when the pack is made free. */
  purchaseCount?: number;
  items: StickerItem[];
};

const pricingBadgeClass = (isPremium?: boolean) =>
  isPremium
    ? 'text-amber-300 border-amber-400/40 bg-amber-500/10'
    : 'text-gray-300 border-white/20 bg-white/5';

/**
 * Curation UI for the GLOBAL live sticker catalog — the packs that become tabs in the app's sticker
 * sheet. Hosts' own uploads never appear here: they are private to that host and the admin routes
 * refuse them.
 */
const LiveStickers: React.FC = () => {
  const navigate = useNavigate();
  const [packs, setPacks] = useState<StickerPack[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState('');
  const [newPackName, setNewPackName] = useState('');
  const [newPackPaid, setNewPackPaid] = useState(false);
  const [newPackPrice, setNewPackPrice] = useState('');
  const [creatingPack, setCreatingPack] = useState(false);
  // Inline Free / Paid editor for one pack at a time.
  const [pricingPackId, setPricingPackId] = useState('');
  const [draftPaid, setDraftPaid] = useState(false);
  const [draftPrice, setDraftPrice] = useState('');
  const [reordering, setReordering] = useState(false);

  const headerWithAuth = useCallback(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      toast.error('No token found. Please log in.');
      return null;
    }
    if (!API_KEY) {
      toast.error('Missing API key. Set REACT_APP_API_KEY in your .env file.');
      return null;
    }
    return getApiHeaders(token);
  }, []);

  const fetchPacks = useCallback(async () => {
    const headers = headerWithAuth();
    if (!headers) return;

    setLoading(true);
    try {
      const res = await fetch(`${liveApiBase}/admin/sticker-packs`, { headers });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to load packs');
      setPacks((json.data?.packs as StickerPack[]) || []);
    } catch (error) {
      toast.error((error as Error).message || 'Failed to fetch packs');
    } finally {
      setLoading(false);
    }
  }, [headerWithAuth]);

  useEffect(() => {
    fetchPacks();
  }, [fetchPacks]);

  const handleCreatePack = async () => {
    if (!newPackName.trim()) {
      toast.error('Pack name is required.');
      return;
    }
    if (newPackPaid) {
      const priceError = validatePackPrice(newPackPrice);
      if (priceError) {
        toast.error(priceError);
        return;
      }
    }
    const headers = headerWithAuth();
    if (!headers) return;

    setCreatingPack(true);
    try {
      const res = await fetch(`${liveApiBase}/admin/sticker-packs`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          name: newPackName.trim(),
          sortOrder: packs.length,
          isActive: true,
          isPremium: newPackPaid,
          priceTokens: newPackPaid ? Number(newPackPrice) : null,
        }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to create pack');
      toast.success('Pack created');
      setNewPackName('');
      setNewPackPaid(false);
      setNewPackPrice('');
      fetchPacks();
    } catch (error) {
      toast.error((error as Error).message || 'Failed to create pack');
    } finally {
      setCreatingPack(false);
    }
  };

  const patchPack = async (packId: string, body: Record<string, unknown>): Promise<boolean> => {
    const headers = headerWithAuth();
    if (!headers) return false;

    setBusyId(packId);
    try {
      const res = await fetch(`${liveApiBase}/admin/sticker-packs/${encodeURIComponent(packId)}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to update pack');
      fetchPacks();
      return true;
    } catch (error) {
      toast.error((error as Error).message || 'Failed to update pack');
      return false;
    } finally {
      setBusyId('');
    }
  };

  const openPricing = (pack: StickerPack) => {
    setPricingPackId(pack.id);
    setDraftPaid(pack.isPremium === true);
    setDraftPrice(pack.isPremium && pack.priceTokens ? String(pack.priceTokens) : '');
  };

  /**
   * Saves Free / Paid and the price. Every change here reaches phones on their next sticker sync, and
   * each direction has a consequence for users, so it is confirmed first (see pricingChangeWarning).
   */
  const savePricing = async (pack: StickerPack) => {
    if (draftPaid) {
      const priceError = validatePackPrice(draftPrice);
      if (priceError) {
        toast.error(priceError);
        return;
      }
    }
    const next = { isPremium: draftPaid, priceTokens: draftPaid ? Number(draftPrice) : null };
    const warning = pricingChangeWarning(pack, next);
    if (!warning) {
      setPricingPackId('');
      return;
    }
    if (!window.confirm(warning)) return;
    const body: Record<string, unknown> = { isPremium: next.isPremium };
    if (next.isPremium) body.priceTokens = next.priceTokens;
    if (await patchPack(pack.id, body)) {
      toast.success(next.isPremium ? `${pack.name} is now ${next.priceTokens} tokens` : `${pack.name} is now free`);
      setPricingPackId('');
    }
  };

  /**
   * Moves a pack one place up or down. The backend takes the WHOLE list in display order (every
   * global pack exactly once) and assigns unique positions, changing as few packs as possible — so
   * apps on V2 sync receive only the moved pack. A 409 means the list changed in another tab:
   * reload rather than retry, since retrying would send the stale order again.
   */
  const movePack = async (index: number, delta: -1 | 1) => {
    const target = index + delta;
    if (target < 0 || target >= packs.length) return;
    const headers = headerWithAuth();
    if (!headers) return;

    const previous = packs;
    const next = [...packs];
    [next[index], next[target]] = [next[target], next[index]];
    setPacks(next);
    setReordering(true);
    try {
      const res = await fetch(`${liveApiBase}/admin/sticker-packs/order`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ packIds: next.map((pack) => pack.id) }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to save pack order');
      setPacks((json.data?.packs as StickerPack[]) || next);
    } catch (error) {
      setPacks(previous);
      toast.error((error as Error).message || 'Failed to save pack order');
      fetchPacks();
    } finally {
      setReordering(false);
    }
  };

  /**
   * Users paid for a bought pack, so the backend refuses to delete it. Hiding is the only
   * alternative today, and it hides the pack from buyers too (there is no "off sale, still usable by
   * buyers" state); their purchase rows are kept, so showing it again restores it.
   */
  const offerHideInstead = (pack: StickerPack, buyers: number) => {
    const who = buyers > 0 ? `${buyers} user${buyers === 1 ? ' has' : 's have'}` : 'Users have';
    if (!pack.isActive) {
      toast.info(`${who} bought "${pack.name}", so it cannot be deleted. It is already hidden.`);
      return;
    }
    if (window.confirm(`${who} bought "${pack.name}", so it cannot be deleted.\n\nHide it instead? It disappears from the app for everyone, buyers included, until you show it again. Their purchases are kept, so they get it back when it is shown.`)) {
      patchPack(pack.id, { isActive: false });
    }
  };

  const handleDeletePack = async (pack: StickerPack) => {
    if ((pack.purchaseCount ?? 0) > 0) {
      offerHideInstead(pack, pack.purchaseCount ?? 0);
      return;
    }
    if (!window.confirm(`Delete pack "${pack.name}"? This cannot be undone.`)) return;
    const headers = headerWithAuth();
    if (!headers) return;

    setBusyId(pack.id);
    try {
      const res = await fetch(`${liveApiBase}/admin/sticker-packs/${encodeURIComponent(pack.id)}`, {
        method: 'DELETE',
        headers,
      });
      const json = await res.json();
      // Bought since this page loaded: same answer as above.
      if (res.status === 409 && (json.code ?? json.error?.code) === 'LIVE_STICKER_PACK_HAS_PURCHASES') {
        setBusyId('');
        offerHideInstead(pack, 0);
        return;
      }
      // The backend refuses to delete a pack that still holds stickers, because they reference it
      // by id and would be orphaned. Surface that message rather than a generic failure.
      if (!res.ok) throw new Error(json.message || 'Failed to delete pack');
      toast.success('Pack deleted');
      fetchPacks();
    } catch (error) {
      toast.error((error as Error).message || 'Failed to delete pack');
    } finally {
      setBusyId('');
    }
  };

  const patchSticker = async (stickerId: string, body: Record<string, unknown>) => {
    const headers = headerWithAuth();
    if (!headers) return;

    setBusyId(stickerId);
    try {
      const res = await fetch(`${liveApiBase}/admin/stickers/${encodeURIComponent(stickerId)}`, {
        method: 'PATCH',
        headers,
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to update sticker');
      fetchPacks();
    } catch (error) {
      toast.error((error as Error).message || 'Failed to update sticker');
    } finally {
      setBusyId('');
    }
  };

  const handleDeleteSticker = async (sticker: StickerItem) => {
    if (!window.confirm(`Delete "${sticker.name}"?`)) return;
    const headers = headerWithAuth();
    if (!headers) return;

    setBusyId(sticker.id);
    try {
      const res = await fetch(`${liveApiBase}/admin/stickers/${encodeURIComponent(sticker.id)}`, {
        method: 'DELETE',
        headers,
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json.message || 'Failed to delete sticker');
      toast.success('Sticker deleted');
      fetchPacks();
    } catch (error) {
      toast.error((error as Error).message || 'Failed to delete sticker');
    } finally {
      setBusyId('');
    }
  };

  const totalStickers = packs.reduce((sum, p) => sum + p.items.length, 0);

  return (
    <div className='min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'>
      <Navbar />

      <div className='max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10'>
        <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8'>
          <div>
            <h1 className='text-2xl sm:text-3xl font-bold text-white'>Live Stickers</h1>
            <p className='text-sm text-gray-400'>
              Packs become tabs in the app's sticker sheet. Hosts' own uploads are private and do not
              appear here.
            </p>
          </div>
          <div className='flex items-center gap-3'>
            <div className='text-sm text-gray-400'>
              {loading ? 'Loading...' : `${packs.length} pack${packs.length !== 1 ? 's' : ''}, ${totalStickers} sticker${totalStickers !== 1 ? 's' : ''}`}
            </div>
            <button
              onClick={() => navigate('/live-stickers/new')}
              disabled={packs.length === 0}
              title={packs.length === 0 ? 'Create a pack first' : ''}
              className='px-4 py-2 text-xs font-semibold rounded-lg bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white hover:opacity-90 transition disabled:opacity-40'
            >
              Upload Sticker
            </button>
          </div>
        </div>

        <div className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-4 mb-8 flex flex-col sm:flex-row gap-3'>
          <input
            value={newPackName}
            onChange={(e) => setNewPackName(e.target.value)}
            placeholder='New pack name (e.g. Classic)'
            className='flex-1 px-4 py-2 text-sm rounded-lg bg-white/10 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-[#FF9C15]'
          />
          <select
            value={newPackPaid ? 'paid' : 'free'}
            onChange={(e) => setNewPackPaid(e.target.value === 'paid')}
            aria-label='Free or paid'
            className='px-3 py-2 text-sm rounded-lg bg-white/10 border border-white/10 text-white focus:outline-none focus:border-[#FF9C15]'
          >
            <option value='free' className='bg-slate-800'>Free</option>
            <option value='paid' className='bg-slate-800'>Paid</option>
          </select>
          {newPackPaid && (
            <input
              type='number'
              min={1}
              step={1}
              value={newPackPrice}
              onChange={(e) => setNewPackPrice(e.target.value)}
              placeholder='Price in tokens'
              aria-label='Price in tokens'
              className='sm:w-40 px-4 py-2 text-sm rounded-lg bg-white/10 border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:border-[#FF9C15]'
            />
          )}
          <button
            onClick={handleCreatePack}
            disabled={creatingPack}
            className='px-4 py-2 text-xs font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition disabled:opacity-40'
          >
            {creatingPack ? 'Creating...' : 'Create Pack'}
          </button>
        </div>

        {loading ? (
          <div className='text-sm text-gray-400'>Fetching packs...</div>
        ) : packs.length === 0 ? (
          <div className='text-sm text-gray-400'>
            No packs yet. Create one above, then upload stickers into it.
          </div>
        ) : (
          <div className='space-y-8'>
            {packs.map((pack, index) => (
              <div
                key={pack.id}
                className='bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5'
              >
                <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4'>
                  <div className='flex items-center gap-3'>
                    {/* Display order. The first pack is the first tab in the app's sticker sheet. */}
                    <div className='flex flex-col gap-1'>
                      <button
                        onClick={() => movePack(index, -1)}
                        disabled={reordering || index === 0}
                        title='Move up'
                        aria-label={`Move ${pack.name} up`}
                        className='w-7 h-6 text-xs rounded bg-white/10 text-white hover:bg-white/20 transition disabled:opacity-30'
                      >
                        ▲
                      </button>
                      <button
                        onClick={() => movePack(index, 1)}
                        disabled={reordering || index === packs.length - 1}
                        title='Move down'
                        aria-label={`Move ${pack.name} down`}
                        className='w-7 h-6 text-xs rounded bg-white/10 text-white hover:bg-white/20 transition disabled:opacity-30'
                      >
                        ▼
                      </button>
                    </div>
                    {pack.iconUrl && (
                      <img src={pack.iconUrl} alt='' className='w-8 h-8 rounded object-cover' />
                    )}
                    <div>
                      <div className='text-base font-semibold text-white'>{pack.name}</div>
                      <div className='text-xs text-gray-500'>
                        #{index + 1} · {pack.id} · order {pack.sortOrder} · {pack.items.length} sticker
                        {pack.items.length !== 1 ? 's' : ''}
                        {(pack.isPremium || (pack.purchaseCount ?? 0) > 0) &&
                          ` · ${pack.purchaseCount ?? 0} buyer${pack.purchaseCount === 1 ? '' : 's'}`}
                      </div>
                    </div>
                  </div>
                  <div className='flex items-center gap-2 flex-wrap'>
                    <span className={`px-2 py-0.5 text-xs rounded-full border ${pricingBadgeClass(pack.isPremium)}`}>
                      {packPriceLabel(pack)}
                    </span>
                    <span
                      className={`px-2 py-0.5 text-xs rounded-full border ${
                        pack.isActive
                          ? 'text-emerald-300 border-emerald-400/40 bg-emerald-500/10'
                          : 'text-red-300 border-red-400/40 bg-red-500/10'
                      }`}
                    >
                      {pack.isActive ? 'Active' : 'Hidden'}
                    </span>
                    <button
                      onClick={() => patchPack(pack.id, { isActive: !pack.isActive })}
                      disabled={busyId === pack.id}
                      className='px-3 py-1.5 text-xs font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition disabled:opacity-40'
                    >
                      {pack.isActive ? 'Hide' : 'Show'}
                    </button>
                    <button
                      onClick={() => {
                        const name = window.prompt('Rename pack', pack.name);
                        if (name && name.trim() && name !== pack.name) {
                          patchPack(pack.id, { name: name.trim() });
                        }
                      }}
                      disabled={busyId === pack.id}
                      className='px-3 py-1.5 text-xs font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition disabled:opacity-40'
                    >
                      Rename
                    </button>
                    <button
                      onClick={() => (pricingPackId === pack.id ? setPricingPackId('') : openPricing(pack))}
                      disabled={busyId === pack.id}
                      className='px-3 py-1.5 text-xs font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition disabled:opacity-40'
                    >
                      Pricing
                    </button>
                    <button
                      onClick={() => handleDeletePack(pack)}
                      disabled={busyId === pack.id}
                      className='px-3 py-1.5 text-xs font-semibold rounded-lg bg-red-500/80 hover:bg-red-500 text-white transition disabled:opacity-40'
                    >
                      Delete
                    </button>
                  </div>
                </div>

                {pricingPackId === pack.id && (
                  <div className='mb-4 bg-white/5 border border-white/10 rounded-xl p-4 flex flex-col sm:flex-row sm:items-end gap-3'>
                    <div>
                      <label className='block text-xs text-gray-400 mb-1'>Pack type</label>
                      <select
                        value={draftPaid ? 'paid' : 'free'}
                        onChange={(e) => setDraftPaid(e.target.value === 'paid')}
                        className='px-3 py-2 text-sm rounded-lg bg-white/10 border border-white/10 text-white focus:outline-none focus:border-[#FF9C15]'
                      >
                        <option value='free' className='bg-slate-800'>Free</option>
                        <option value='paid' className='bg-slate-800'>Paid</option>
                      </select>
                    </div>
                    {draftPaid && (
                      <div>
                        <label className='block text-xs text-gray-400 mb-1'>Price (tokens)</label>
                        <input
                          type='number'
                          min={1}
                          step={1}
                          value={draftPrice}
                          onChange={(e) => setDraftPrice(e.target.value)}
                          className='w-40 px-3 py-2 text-sm rounded-lg bg-white/10 border border-white/10 text-white focus:outline-none focus:border-[#FF9C15]'
                        />
                      </div>
                    )}
                    <p className='flex-1 text-xs text-gray-400'>
                      {draftPaid
                        ? 'Free for paying Plus subscribers (not trials); everyone else unlocks it by buying it with tokens. Every sticker in it becomes premium.'
                        : 'Everyone can use a free pack.'}
                    </p>
                    <div className='flex gap-2'>
                      <button
                        onClick={() => setPricingPackId('')}
                        className='px-3 py-1.5 text-xs font-semibold rounded-lg bg-white/10 text-white hover:bg-white/20 transition'
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => savePricing(pack)}
                        disabled={busyId === pack.id}
                        className='px-3 py-1.5 text-xs font-semibold rounded-lg bg-gradient-to-r from-[#FF9C15] to-[#F42026] text-white hover:opacity-90 transition disabled:opacity-40'
                      >
                        Save
                      </button>
                    </div>
                  </div>
                )}

                {pack.items.length === 0 ? (
                  <div className='text-xs text-gray-500'>Empty pack — a pack with no active stickers is hidden in the app.</div>
                ) : (
                  <div className='grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4'>
                    {pack.items.map((item) => (
                      <div
                        key={item.id}
                        className='bg-white/5 border border-white/10 rounded-xl overflow-hidden'
                      >
                        {/* Checkerboard behind the art: a sticker with no alpha looks identical to
                            one with alpha on a flat background, and that is exactly the mistake
                            that ships an opaque white box over the video. */}
                        <div
                          className='h-28 flex items-center justify-center p-2'
                          style={{
                            backgroundImage:
                              'linear-gradient(45deg, #334155 25%, transparent 25%), linear-gradient(-45deg, #334155 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #334155 75%), linear-gradient(-45deg, transparent 75%, #334155 75%)',
                            backgroundSize: '12px 12px',
                            backgroundPosition: '0 0, 0 6px, 6px -6px, -6px 0px',
                          }}
                        >
                          <img src={item.imageUrl} alt={item.name} className='max-h-full max-w-full object-contain' />
                        </div>
                        <div className='p-3 space-y-1.5'>
                          <div className='text-xs font-semibold text-white truncate'>{item.name}</div>
                          <div className='flex items-center gap-1.5 flex-wrap'>
                            <span className='px-1.5 py-0.5 text-[10px] rounded bg-white/10 text-gray-300'>
                              {item.kind}
                            </span>
                            <span
                              className={`px-1.5 py-0.5 text-[10px] rounded ${
                                item.isActive ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'
                              }`}
                            >
                              {item.isActive ? 'active' : 'hidden'}
                            </span>
                            {item.isPremium && (
                              <span className='px-1.5 py-0.5 text-[10px] rounded bg-amber-500/20 text-amber-300'>premium</span>
                            )}
                          </div>
                          <div className='flex gap-1.5 pt-1'>
                            <button
                              onClick={() => navigate(`/live-stickers/${encodeURIComponent(item.id)}/edit`, { state: { sticker: item, packs } })}
                              className='flex-1 px-2 py-1 text-[10px] font-semibold rounded bg-white/10 text-white hover:bg-white/20 transition'
                            >
                              Details
                            </button>
                            <button
                              onClick={() => patchSticker(item.id, { isActive: !item.isActive })}
                              disabled={busyId === item.id}
                              className='flex-1 px-2 py-1 text-[10px] font-semibold rounded bg-white/10 text-white hover:bg-white/20 transition disabled:opacity-40'
                            >
                              {item.isActive ? 'Hide' : 'Show'}
                            </button>
                            <button
                              onClick={() => handleDeleteSticker(item)}
                              disabled={busyId === item.id}
                              className='flex-1 px-2 py-1 text-[10px] font-semibold rounded bg-red-500/70 hover:bg-red-500 text-white transition disabled:opacity-40'
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LiveStickers;
